// Fixture time helpers — small shared utilities for the Select-Fixture
// modal so kick-off displays show both UTC (operator-facing canonical) and
// the user's local-zone equivalent, and the "Upcoming" filter can pick out
// future + in-flight matches.
//
// Mirrors aether's Fixture.tsx logic so the two surfaces agree on what
// counts as upcoming.

// Generous window to keep a live match in the "upcoming" filter past
// kick-off until full time + stoppage + injury time. Mirrors aether.
const ACTIVE_WINDOW_MS = 3 * 60 * 60 * 1000;

// Status strings that mean the match is done — short-circuits the active
// window so a finished but recent fixture isn't kept in Upcoming.
const FINISHED_STATUS_RE =
  /FT|FINISHED|FULL[ _]?TIME|ENDED|POSTPONED|CANCELLED|ABANDONED/i;

/**
 * Format a DDS `fixture_date` (YYYY-MM-DD or full ISO YYYY-MM-DDT…Z) as
 * DD/MM/YYYY for user-facing display. Returns "" on missing/unparseable
 * input so callers can fall through to an empty cell without a JS error.
 */
export const formatDateDDMMYYYY = (input) => {
  if (!input) return "";
  const datePart = String(input).replace("Z", "").split("T")[0];
  const [y, m, d] = datePart.split("-");
  if (!y || !m || !d) return "";
  return `${d}/${m}/${y}`;
};

/**
 * Combine the DDS `fixture_date` (YYYY-MM-DD[T...]Z) and `kick_off_time`
 * (HH:MM[:SS][Z]) into a UTC timestamp in ms. Returns NaN if either field
 * is missing.
 */
export const parseFixtureTs = (fixtureDate, kickOffTime) => {
  if (!fixtureDate || !kickOffTime) return NaN;
  const date = String(fixtureDate).replace("Z", "").split("T")[0];
  const time = String(kickOffTime).replace("Z", "").slice(0, 8);
  return new Date(`${date}T${time}Z`).getTime();
};

/**
 * Upcoming-or-live predicate. Future kickoffs always pass; past kickoffs
 * pass only if they're within ACTIVE_WINDOW_MS AND the fixture isn't
 * explicitly finished/cancelled/etc.
 */
export const isUpcomingOrLive = (ts, status, now = Date.now()) => {
  if (!Number.isFinite(ts)) return false;
  if (ts >= now) return true;
  if (now - ts > ACTIVE_WINDOW_MS) return false;
  return !FINISHED_STATUS_RE.test(String(status || ""));
};

/**
 * Parse a "MM:SS" clock string into total seconds. Returns 0 for
 * missing/unparseable input.
 */
export const clockTimeToSeconds = (clockTime) => {
  if (!clockTime) return 0;
  const parts = clockTime.toString().split(":").map(Number);
  const m = parts[0] || 0;
  const s = parts[1] || 0;
  const total = m * 60 + s;
  return isNaN(total) ? 0 : total;
};

/**
 * Format a UTC timestamp in the browser's local timezone.
 * @returns {{ time: string, tz: string } | null}
 *   time — 24h HH:MM in local zone
 *   tz   — short zone name as the browser chooses to render it (e.g. BST, IST, GMT+5)
 */
export const formatLocalTime = (ts) => {
  if (!Number.isFinite(ts)) return null;
  const d = new Date(ts);
  if (isNaN(d.getTime())) return null;
  const time = d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  // Extract zone suffix from a separate call so it stays consistent regardless
  // of whether the user's locale embeds it in the 24h render.
  const tz = d
    .toLocaleTimeString(undefined, { timeZoneName: "short" })
    .split(" ")
    .pop();
  return { time, tz };
};
